/**
 * Export utilities for CSV generation
 */

// Format date as "Jan 15 2026"
// Parses year/month/day components directly instead of handing the raw
// string to `new Date()`: a date-only ISO string ("2026-07-15") is parsed by
// the Date constructor as UTC midnight, which then renders as the day before
// in any timezone behind UTC (e.g. US Central/Eastern) once toLocaleDateString
// converts it to local time. Building the Date from explicit (year, month,
// day) always anchors it to local midnight, so no shift happens regardless
// of which format ('Requested Date' shows up as either YYYY-MM-DD or
// M/D/YYYY depending on the row's origin) or timezone.
const formatDateForExport = (dateStr) => {
  if (!dateStr) return '';
  try {
    const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);

    let year, month, day;
    if (isoMatch) {
      [, year, month, day] = isoMatch;
    } else if (slashMatch) {
      [, month, day, year] = slashMatch;
    } else {
      return dateStr;
    }

    const date = new Date(Number(year), Number(month) - 1, Number(day));
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  } catch {
    return dateStr;
  }
};

// Format time as "9:00 AM"
const formatTimeForExport = (timeStr) => {
  if (!timeStr) return '';
  try {
    // Handle both 24hr and 12hr formats
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }

    // Convert 24hr to 12hr
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch {
    return timeStr;
  }
};

/**
 * Escapes a single CSV field:
 *  1. Formula-injection guard — a spreadsheet can interpret a field starting
 *     with =, @, or a sign (+/-) as a formula/DDE. We prefix an apostrophe to
 *     force text, but only for formula-shaped input: a leading =/@, or a
 *     leading +/- immediately followed by a non-space char (e.g. "-2+3").
 *     Natural-language bullets like "- bring nets" (sign then space) are left
 *     intact so legitimate notes aren't mangled in the Assignr import.
 *  2. Quoting — wrap in quotes if the field contains a comma, a quote, or ANY
 *     line break (\n or a lone \r); a bare carriage return would otherwise
 *     split the record on import.
 */
export const escapeCsvField = (field) => {
  let fieldStr = String(field);

  if (/^[=@]/.test(fieldStr) || /^[+-]\S/.test(fieldStr)) {
    fieldStr = `'${fieldStr}`;
  }

  if (/[",\n\r]/.test(fieldStr)) {
    return '"' + fieldStr.replace(/"/g, '""') + '"';
  }
  return fieldStr;
};

/**
 * Assignr rejects two games for the same team name at the same date/time —
 * which is exactly what a multi-slot request produces (one home team, several
 * parallel fields). These helpers serialize the Home Team name in the CSV
 * only (sheet data and emails keep the clean name) by appending the field
 * number, e.g. "Tigers FC (F3)" / "Tigers FC (F4)".
 *
 * The conflict pool includes Scheduled rows too, not just the Processing rows
 * being exported: a game exported today can collide in Assignr with one
 * already imported from an earlier batch.
 */
const homeTeamConflictKey = (req) => {
  const team = (req['Home Team'] || '').trim().toLowerCase();
  const date = formatDateForExport(req['Requested Date'] || '');
  const time = formatTimeForExport(req['Time'] || '');
  if (!team || !date || !time) return null;
  return `${team}|${date}|${time}`;
};

export const buildHomeTeamConflictKeys = (requests) => {
  const counts = {};
  requests.forEach((req) => {
    const status = req['Request Status'];
    if (status !== 'Processing' && status !== 'Scheduled') return;
    const key = homeTeamConflictKey(req);
    if (key) counts[key] = (counts[key] || 0) + 1;
  });
  return new Set(Object.keys(counts).filter((k) => counts[k] > 1));
};

export const homeTeamForExport = (req, conflictKeys) => {
  const base = req['Home Team'] || '';
  const key = homeTeamConflictKey(req);
  if (!key || !conflictKeys.has(key)) return base;
  const field = String(req['Sub Venue'] || '').trim();
  return field ? `${base} (F${field})` : base;
};

/**
 * Export Processing requests to CSV
 * Downloads a CSV file with all Processing status games
 */
export const exportProcessingToCSV = (requests) => {
  const processingRequests = requests.filter(r => r['Request Status'] === 'Processing');

  if (processingRequests.length === 0) {
    alert('No Processing requests to export.');
    return;
  }

  // Define CSV headers to match Assignr import format
  const headers = [
    'Game ID',
    'Date',
    'Time',
    'Venue',
    'Sub-Venue',
    'Age Group',
    'Gender',
    'Home Team',
    'Away Team',
    'League',
    'Game Type',
    'Pattern',
    'Notes',
    'Assignor Notes'
  ];

  // Create CSV content
  let csvContent = headers.join(',') + '\n';

  const conflictKeys = buildHomeTeamConflictKeys(requests);

  processingRequests.forEach(req => {
    const row = [
      req['Game Number'] || '',
      formatDateForExport(req['Requested Date']) || '',
      formatTimeForExport(req['Time']) || '',
      req['Venue'] || '',
      req['Sub Venue'] || '',
      req['Age Group'] || '',
      req['Gender'] || '',
      homeTeamForExport(req, conflictKeys),
      req['Away Team'] || '',
      'TSC', // League - always TSC
      req['Game Type'] || '',
      req['Referee Pattern'] || '',
      // Notes: Contact info + Special Notes
      [
        req['Contact Name'] ? `Contact: ${req['Contact Name']}` : '',
        req['Email Address'] ? `Email: ${req['Email Address']}` : '',
        req['Contact Phone'] ? `Phone: ${req['Contact Phone']}` : '',
        req['Special Notes'] || ''
      ].filter(Boolean).join(' | '),
      req['Admin Notes'] || ''
    ].map(escapeCsvField);

    csvContent += row.join(',') + '\n';
  });

  // Add 30 blank rows as buffer
  for (let i = 0; i < 30; i++) {
    csvContent += ',,,,,,,,,,,,,\n';
  }

  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  const dateStr = new Date().toISOString().split('T')[0];
  link.setAttribute('href', url);
  link.setAttribute('download', `TSC_Processing_Games_${dateStr}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  console.log(`✓ Exported ${processingRequests.length} Processing games to CSV`);
};
